import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEOHead, { CourseSchema } from '../components/SEOHead';
import { sanitizeHtml, stripHtmlToText } from '../utils/sanitize';
import { isProgramPublic } from '../utils/programVisibility';
import { isPrivateLesson, getLowestSessionPrice } from '../utils/privateLesson';
import { PrivateLessonBanner } from '../components/PrivateLessonBanner';
import { mediaUrl, resolveImage } from '../utils/mediaUrl';

const API_URL = "";

export const AdultClasses = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/programs`);
        if (res.ok) {
          setPrograms(await res.json());
        }
      } catch (err) {
        console.error('Error fetching programs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  // Sort by day-of-week (Mon→Sat first, Sun and unknown last) then AM before PM.
  // Parses the program.schedule free-text field, e.g. "Thursdays, 9:15 AM - 1:15 PM"
  // or "Wednesdays or Thursdays, 4:00 PM - 5:30 PM" (first day wins).
  const getScheduleSortKey = (schedule) => {
    const s = String(schedule || '');
    const dayOrder = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };
    const dayMatch = s.toLowerCase().match(/\b(mon|tue|wed|thu|fri|sat|sun)/);
    const day = dayMatch ? dayOrder[dayMatch[1]] : 99;
    // First time token: "9:15 AM", "5:45 PM", or "Noon"
    let minutes = 24 * 60; // unknown sorts last within a day
    const noon = /\bnoon\b/i.test(s);
    const mid = /\bmidnight\b/i.test(s);
    const tm = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
    if (noon) {
      minutes = 12 * 60;
    } else if (mid) {
      minutes = 0;
    } else if (tm) {
      let h = parseInt(tm[1], 10) % 12;
      const m = tm[2] ? parseInt(tm[2], 10) : 0;
      if (tm[3].toLowerCase() === 'pm') h += 12;
      minutes = h * 60 + m;
    }
    return day * 10000 + minutes;
  };

  const adultClasses = programs
    .filter(program =>
      program.category !== 'Youth' &&
      !program.isPayPal &&
      program.title !== 'Coppini Birthday' &&
      isProgramPublic(program)
    )
    .sort((a, b) => {
      // Private lessons run year-round — list them after the scheduled classes
      const pa = isPrivateLesson(a) ? 1 : 0;
      const pb = isPrivateLesson(b) ? 1 : 0;
      if (pa !== pb) return pa - pb;
      const ka = getScheduleSortKey(a.schedule);
      const kb = getScheduleSortKey(b.schedule);
      if (ka !== kb) return ka - kb;
      // Stable tiebreaker: by title
      return String(a.title || '').localeCompare(String(b.title || ''));
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading classes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="adult-classes-page">
      <SEOHead
        title="Adult Art Classes — Drawing & Painting"
        description="Classical drawing and painting classes for adults at The Coppini Academy of Fine Art in San Antonio. Atelier-style instruction in figure drawing, portraiture, and oil painting."
        canonicalPath="/classes/adult"
      />
      {adultClasses.map((program) => (
        <CourseSchema
          key={`course-schema-${program.id}`}
          name={program.title}
          description={stripHtmlToText(program.description).slice(0, 500) || program.title}
          price={isPrivateLesson(program) ? undefined : program.price}
          startDate={program.startDate}
          endDate={program.endDate}
          instructor={program.instructor}
        />
      ))}
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-stone-900 mb-6">Adult Classes</h1>
          <p className="text-xl text-stone-600">
            Comprehensive instruction in classical fine art for adult learners
          </p>
          <p className="text-stone-500 mt-4">
            <Link to="/classes/kids" className="text-cafa-red hover:text-cafa-red-900 underline">
              Looking for Kids Classes? →
            </Link>
          </p>
        </div>
      </section>

      {/* Classes */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {adultClasses.length === 0 ? (
            <div className="text-center text-stone-600 py-12">
              <p>No adult classes are currently scheduled. Please check back later!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {adultClasses.map((program) => {
                const cardImage = resolveImage(program, 'image_ref', 'images', 'classImage', 'instructorImage');
                return (
                <Card key={program.id} className="border-stone-200 hover:shadow-lg transition-shadow flex flex-col overflow-hidden" data-testid={`class-card-${program.id}`}>
                  {/* Class / Instructor Image (fallback chain mirrors KidsClasses) */}
                  {cardImage && (
                    <div className="h-48 overflow-hidden bg-stone-100">
                      <img
                        src={mediaUrl(cardImage, 'card')}
                        alt={program.title}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 bg-cafa-red-100 text-cafa-red-900 text-xs font-medium rounded-full">
                        {program.category}
                      </span>
                      <span className="text-sm text-stone-500">{program.level}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900 mb-3">{program.title}</h3>
                    <div
                      className="text-sm text-stone-600 mb-4 flex-grow prose prose-sm prose-stone max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(program.description) }}
                    />
                    {isPrivateLesson(program) && <PrivateLessonBanner program={program} />}
                    <div className="space-y-2 text-sm text-stone-500 mb-4">
                      {!isPrivateLesson(program) && (
                        <>
                          <p><strong className="text-stone-700">Duration:</strong> {program.duration}</p>
                          <p><strong className="text-stone-700">Schedule:</strong> {program.schedule}</p>
                        </>
                      )}
                      <p><strong className="text-stone-700">Instructor:</strong> {program.instructor}</p>
                    </div>
                    
                    {isPrivateLesson(program) ? (
                      <div className="pt-4 border-t border-stone-200">
                        <div className="flex items-baseline justify-between mb-3">
                          <span className="text-2xl font-bold text-cafa-red">From ${getLowestSessionPrice(program)}</span>
                          <span className="text-xs text-stone-500">per session</span>
                        </div>
                        <Button
                          onClick={() => navigate(`/register-class?class=${program.id}`)}
                          className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                          data-testid={`book-private-btn-${program.id}`}
                        >
                          Book a Private Lesson
                        </Button>
                      </div>
                    ) : program.isPayPal ? (
                      <div className="pt-4 border-t border-stone-200">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-sm text-stone-600">Members: <span className="font-bold text-cafa-red">${program.memberPrice}</span></p>
                            <p className="text-sm text-stone-600">Non-Members: <span className="font-bold text-stone-700">${program.nonMemberPrice}</span></p>
                          </div>
                        </div>
                        <Button 
                          asChild
                          className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                          data-testid={`register-btn-${program.id}`}
                        >
                          <Link to={`/register-open-session?session=${program.id}`}>
                            Register
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                        <span className="text-2xl font-bold text-cafa-red">${program.price}</span>
                        {/* Enrollment Managed Classes - Live Registration */}
                        {program.class_type === 'enrollment_managed' ? (
                          <Button 
                            onClick={() => navigate(`/register-class?class=${program.id}`)}
                            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                            data-testid={`enroll-btn-${program.id}`}
                          >
                            Enroll Now
                          </Button>
                        ) : program.payment_method === 'paypal' && program.price > 0 ? (
                          <Button
                            onClick={() => navigate(`/register-class?class=${program.id}`)}
                            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                            data-testid={`paypal-btn-${program.id}`}
                          >
                            Register &amp; Pay
                          </Button>
                        ) : program.class_type === 'space_rental' ? (
                          <Button 
                            asChild
                            variant="outline"
                            className="border-stone-300 text-stone-700"
                            data-testid={`inquire-btn-${program.id}`}
                          >
                            <a href={`mailto:${program.instructorEmail}?subject=Space Rental Inquiry: ${program.title}`}>
                              Inquire
                            </a>
                          </Button>
                        ) : (
                          /* Informational Classes - Contact for more info */
                          <Button 
                            asChild
                            variant="outline"
                            className="border-stone-300 text-stone-700"
                            data-testid={`learn-btn-${program.id}`}
                          >
                            <a href={`mailto:${program.instructorEmail}?subject=Class Inquiry: ${program.title}`}>
                              Learn More
                            </a>
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-stone-900 mb-4">Ready to Begin Your Art Journey?</h2>
          <p className="text-stone-600 mb-8">
            Membership is required for enrollment in most classes. Join our community of artists today.
          </p>
          <Button asChild className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            <Link to="/membership">Become a Member</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
